import Logo from "./../../assets/logo-login.svg";
import Button from "../../components/Buttons/Button";
import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { secondary } from "../../theme";
import { useApi } from "../../hooks/api";
import { useForm } from "react-hook-form";
import { BsArrowLeft } from "react-icons/bs";
import { useGlobal } from "../../hooks/global";
import { IGroupProps } from "../../interfaces";
import { ModalLoading } from "../../components/Loadings/ModalLoading";
import { GenericForm, IGenericFormRefProps } from "../../components/GenericForm";
import { validateCEP, validateCNPJ, validateEmail, validatePhone } from "../../utils/validatesFields";
import { Container, ContainerBottomButtons, ContainerImage, ContainerLink, ContainerTop, Description, Form, Link, Title, LogoImg, Content, ContainerTerms, AnchorTerms, Span } from "./style";
import { Divider, Flex, Progress, Text } from "@mantine/core";
import { managedAccommodationsOptions, positionTypeOptions } from "../../utils/options";
import zxcvbn from "zxcvbn";
import { FiAlertCircle } from "react-icons/fi";
import { Box } from "@material-ui/core";
import { MagicButton } from "../../components/Buttons/MagicButton";
import DEFAULT_LOGO from '../../assets/web-check-in-default-logo.png';
import { useNewAccountStore } from '../../store/newAccountStore'

const terms_url = "https://webcheckin.app/termos-de-uso/"

interface INewAccountProps { }

export const NewAccount: React.FC<INewAccountProps> = () => {
    const [loading, setLoading] = useState(false)
    const [loadingCEP, setLoadingCEP] = useState(false)
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [acceptTerms, setAcceptTerms] = useState(false);

    const {
        step, setStep,
        userData, setUserData,
        responsibleData, setResponsibleData,
        reset: resetStore
    } = useNewAccountStore();

    const { register, handleSubmit, control, watch, formState: { errors }, setError, setValue, trigger, reset } = useForm({
        mode: "all"
    });

    const _form = watch()

    const form_ref = useRef<IGenericFormRefProps>(null)
    const buttonSubmitRef = useRef<HTMLButtonElement>(null)

    const { notify, setTheme, theme } = useGlobal()
    const { api, signIn, consultsServices } = useApi();

    const changeCEP = useCallback(async (search) => {
        search = search.replace(/\D/g, "")
        if (search.length === 8) {
            setLoadingCEP(true)

            const { address, city, state } = await consultsServices.address({ search })

            if (address) {
                setValue("street", address.logradouro)
                setValue("neighborhood", address.bairro)
                setValue("address_complement", address.complemento)
            }

            if (city) setValue("city_id", { label: city.name, value: city.id, this: city })

            if (state) setValue("state_id", { label: state.name, value: state.id, initials: state.initials, this: state })

            setLoadingCEP(false)

            trigger()
        }
    }, [])

    const changePassword = useCallback((password) => {
        const score = zxcvbn(password).score

        setPasswordStrength(score);
    }, []);

    const getStrengthLabel = (score: number) => {
        const labels = ["Muito fraca", "Fraca", "Média", "Boa", "Forte"];
        return labels[score];
    };

    const getStrengthColor = (score: number) => {
        const colors = ["gray", "red", "yellow", "green", "teal"];
        return colors[score];
    };

    const strengthLabel = getStrengthLabel(passwordStrength);
    const strengthColor = getStrengthColor(passwordStrength);

    const transformDefaultLogoInBase64 = useCallback(async () => {
        try {
            const response = await fetch(DEFAULT_LOGO);
            const blob = await response.blob();

            const file = new File([blob], "web-check-in-default-logo.png", { type: blob.type });

            const formData = new FormData();
            formData.append("photo", file);

            const { data } = await api.post("/photos/transform", formData);

            return data.complete
        } catch (err) {
            console.error(err);
        }
    }, []);

    const groupsStep1: IGroupProps[] = [
        {
            name: "",
            label: "",
            fields: [
                [
                    {
                        name: "surname",
                        label: "Responsável legal",
                        type: "input",
                        required: true
                    }
                ],
                [
                    {
                        name: "position",
                        label: "Cargo",
                        type: "select-fixed",
                        options: positionTypeOptions,
                        required: true
                    },
                ],
                [
                    {
                        name: "phone",
                        label: "Telefone",
                        type: "input",
                        mask: "phone",
                        validate: validatePhone,
                        required: true
                    },
                    {
                        name: "email",
                        label: "Email",
                        type: "input",
                        validate: validateEmail,
                        required: true
                    }
                ],
                [
                    {
                        name: "password",
                        label: "Senha",
                        type: "input",
                        required: true,
                        isPassword: true,
                        noUpperCase: true,
                        executeOnChange: async (e) => changePassword(e),
                    },
                    {
                        name: "confirm-password",
                        label: "Repetir senha",
                        type: "input",
                        required: true,
                        isPassword: true,
                        validate: async (value: any) => {
                            const password = watch("password")
                            if (value !== password) return "As senhas não conferem."
                            return true
                        },
                        noUpperCase: true
                    }
                ]
            ]
        }
    ];

    const groupsStep2: IGroupProps[] = [
        {
            name: "",
            label: "",
            fields: [
                [
                    {
                        name: "legal_name",
                        label: "Razão Social",
                        type: "input",
                        required: true
                    }
                ],
                [
                    {
                        name: "trade_name",
                        label: "Nome Fantasia",
                        type: "input",
                        required: true
                    }
                ],
                [
                    {
                        name: "cnpj",
                        label: "CNPJ",
                        type: "input",
                        mask: "cnpj",
                        validate: validateCNPJ,
                        required: true
                    },
                    {
                        name: "managed_accommodations",
                        label: "Acomodações administradas",
                        type: "select-fixed",
                        options: managedAccommodationsOptions,
                    }
                ]
            ]
        }
    ];

    const groupsStep3: IGroupProps[] = [
        {
            name: "",
            label: "",
            fields: [
                [
                    {
                        name: "zip_code",
                        label: "CEP",
                        type: "input",
                        mask: "cep",
                        validate: validateCEP,
                        executeOnChange: changeCEP,
                        required: true,
                    },
                ],
                [
                    {
                        name: "state_id",
                        label: "Estado",
                        type: "select-single-no-creatable",
                        isClearable: true,
                        searchOptions: consultsServices.state_id,
                        executeOnChange: async () => setValue("city_id", null),
                        required: true,
                    },
                    {
                        name: "city_id",
                        label: "Cidade",
                        type: "select-single-no-creatable",
                        isClearable: true,
                        searchOptions: consultsServices.city_id,
                        additionalsQueries: (data: any) => ({ state_id: data?.state_id?.value }),
                        getIsDisabled: (data: any) => !data?.state_id,
                        required: true,
                    }
                ]
            ]
        }
    ];

    const handleStepSubmit = useCallback(async (data: any) => {
        if (step === 1) {
            setLoading(true);
            try {
                const userPayload = {
                    responsible: {
                        surname: data?.surname,
                        position: data?.position?.value,
                        phone: data?.phone,
                        email: data?.email,
                        password: data?.password,
                    }
                };
                await api.post("/create-user", userPayload);

                setUserData(userPayload);
                setStep(2);
                reset();
            } catch (err: any) {
                const error = err.response ? err.response.data : "SERVER ERROR";
                if (error.field) setError(error.field, { type: "manual", message: error.detail });
                if (error?.title) notify(error.title, "error")
                else notify("ERRO INTERNO DO SISTEMA", "error")
            }
            setLoading(false);
        } else if (step === 2) {
            setResponsibleData({
                legal_name: data?.legal_name,
                trade_name: data?.trade_name,
                cnpj: data?.cnpj,
                managed_accommodations: data?.managed_accommodations?.value,
            });
            setStep(3);
            reset();
        } else if (step === 3) {
            setLoading(true);
            try {
                const logo = await transformDefaultLogoInBase64();

                const businessPayload = {
                    business: {
                        ...responsibleData,
                        zip_code: data?.zip_code,
                        state_id: data?.state_id?.value,
                        city_id: data?.city_id?.value,
                        logo,
                    },
                    ...userData,
                };

                await api.post("/create-business", businessPayload);

                await signIn({ email: userData?.responsible?.email, password: userData?.responsible?.password })
                notify("Conta criada com sucesso!", "success");
                resetStore();
            } catch (err: any) {
                const error = err.response ? err.response.data : "SERVER ERROR";
                if (error.field) setError(error.field, { type: "manual", message: error.detail });
                if (error?.title) notify(error.title, "error")
                else notify("ERRO INTERNO DO SISTEMA", "error")
            }
            setLoading(false);
        }
    }, [step, api, responsibleData, notify, setError, reset, setStep, setUserData, setResponsibleData, userData, signIn, transformDefaultLogoInBase64, resetStore]);

    useEffect(() => {
        if (theme != 'light') {
            setTheme('light')
        }
    }, [])

    return (
        <Container>
            <ContainerImage />
            <Content>
                <ContainerTop>
                    <ContainerLink>
                        <Link
                            to="/login"
                            children={(
                                <>
                                    <BsArrowLeft />
                                    Voltar para o login
                                </>
                            )}
                        />
                    </ContainerLink>
                    <Flex pb='5px'>
                        <LogoImg src={Logo} height={38} />
                    </Flex>
                    <Title>
                        Preencha o formulário e
                        experimente <Span>gratuitamente</Span>.
                    </Title>
                    <Description>
                        Preencha o formulário e faça um teste grátis de 7 dias.
                        Descubra como a nossa plataforma pode ajudar a sua empresa!
                    </Description>
                </ContainerTop>

                <Flex
                    direction="column"
                    h="100%"
                    justify="flex-start"
                    style={{
                        paddingBottom: window.innerHeight < 700 ? 360 : window.innerHeight < 750 ? 270 :  window.innerHeight < 800 ? 290: window.innerHeight < 845 ? 180 : window.innerHeight < 890 ? 170 : window.innerHeight < 900 ? 130 : window.innerHeight < 1000 ? 100 : 0,

                    }}
                >
                    <div style={{ margin: '24px 0 8px 0', fontWeight: 600, fontSize: 18, fontFamily: 'Poppins', textAlign: 'center' }}>
                        {step === 1 && 'Dados do Usuário'}
                        {step === 2 && 'Dados da Empresa'}
                        {step === 3 && 'Endereço da Empresa'}
                    </div>
                    <Flex justify="center">
                        <Divider w={300} />
                    </Flex>
                    <Form onSubmit={handleSubmit(handleStepSubmit)} style={{ zIndex: 999, overflow: 'visible' }}>
                        <GenericForm
                            ref={form_ref}
                            groups={step === 1 ? groupsStep1 : step === 2 ? groupsStep2 : groupsStep3}
                            _form={_form}
                            control={control}
                            trigger={trigger}
                            errors={errors}
                            setValue={setValue}
                            register={register}
                            disabled={loadingCEP}
                        />
                        {step === 1 && (
                            <Box mt={3}>
                                <Progress
                                    value={(passwordStrength + 1) * 20}
                                    color={strengthColor}
                                    mt="xs"
                                    radius="xl"
                                    sx={{ transition: "width 4.5s ease-in-out, background-color 4.3s ease-in-out" }}
                                />
                                <Text size="sm" color={strengthColor} mt={4} display="flex" style={{ alignItems: 'center', fontSize: 13, fontFamily: 'Poppins' }}>
                                    <FiAlertCircle size={16} style={{ marginRight: 3 }} />
                                    {strengthLabel}
                                </Text>
                            </Box>
                        )}
                        {step === 1 && (
                            <div
                                style={{
                                    marginTop: 30,
                                    marginLeft: -5,
                                    display: "flex",
                                    gap: 5,
                                    alignItems: "flex-start",
                                    cursor: "pointer",
                                }}
                                onClick={() => setAcceptTerms((old) => !old)}
                            >
                                <MagicButton
                                    checked={acceptTerms}
                                    onClick={() => setAcceptTerms((old) => !old)}
                                    theme="light"
                                    type="checkbox"
                                    label={""}
                                    labelStyle={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        fontSize: 13,
                                    }}
                                    background={secondary.light}
                                />
                                <div
                                    style={{
                                        marginTop: -11,
                                        cursor: "pointer",
                                        userSelect: "none",
                                        fontSize: 13,
                                    }}
                                >
                                    Ao criar sua conta, você concorda com os
                                    <AnchorTerms
                                        target="_blank"
                                        href={terms_url}
                                        children={<Span> termos de uso</Span>}
                                    />
                                    .
                                </div>
                            </div>
                        )}
                        <button
                            ref={buttonSubmitRef}
                            type="submit"
                            style={{ display: "none" }}
                        />
                    </Form>
                </Flex>

                <ContainerBottomButtons>
                    <Button
                        color="#FFF"
                        background={secondary.light}
                        onClick={() => buttonSubmitRef.current?.click()}
                        style={{ fontSize: 15, padding: "8px 16px" }}
                        children={step === 3 ? "Concluir" : "Próximo"}
                        disabled={step === 1 ? (loadingCEP || loading || !acceptTerms) : (loadingCEP || loading)}
                    />
                </ContainerBottomButtons>
            </Content>
            <ModalLoading loading={loading} theme="light" />
        </Container>
    )
}